import I18n from 'i18n-js';
import React, { useContext } from 'react';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import { ShippingRegionSelector, ILegacyApiShippingRegion } from '@reverbdotcom/commons/src/shipping/shipping_region_selector';
import { RCLoadingBars } from '@reverbdotcom/commons/src/rc/RCLoadingBars';
import { EVERYWHERE_CODE } from '@reverbdotcom/commons/src/constants';

import { buildBlankShippingRate, upsertShippingRate } from './ShippingForm';
import { SellFormContext } from '../../SellFormContext';
import { Field, ActionType } from '../../sellFormReducer';
import { SellFormSection } from '../SellFormSection';
import { V3 } from '../../../../api_request';
import UrlHelpers from '../../../../shared/url_helpers';
import SellFormCallout from '../SellFormCallout';
import TrendingSvg from '@reverbdotcom/commons/design-system/icons/core/trending-up-arrow.svg';
import InternationalShippingPrice from './InternationalShippingPrice';

function InternationalShippingCard() {
  const { formState, dispatch, sellFormViewData, loading } = useContext(SellFormContext);
  const [areShippingRegionsLoaded, setAreShippingRegionsLoaded] = React.useState(false);
  const [shippingRegions, setShippingRegions] = React.useState<ILegacyApiShippingRegion[]>([]);

  const { shippingRegionSettings } = sellFormViewData.me.shop;
  const domesticRegion = shippingRegionSettings.find(({ isDomestic }) => isDomestic);
  const nonDomesticShippingRates = formState[Field.SHIPPING_RATES].filter((sr) => sr.regionCode !== domesticRegion.regionCode);

  React.useEffect(() => {
    V3.get(UrlHelpers.shippingRegionsPath).done((response) => {
      setShippingRegions(response.shipping_regions);
      setAreShippingRegionsLoaded(true);
    });
  }, [setShippingRegions]);

  const suggestedShippingRegions = shippingRegionSettings.filter(({ isDomestic, regionCode }) => !isDomestic && regionCode !== EVERYWHERE_CODE);

  if (!areShippingRegionsLoaded) {
    return (
      <SellFormSection.Card>
        <div className="scaling-padding-2 d-flex fx-justify-center bg-white">
          <RCLoadingBars size="large" />
        </div>
      </SellFormSection.Card>
    );
  }

  return (
    <SellFormSection.Card>
      <SellFormSection.FormSection title={I18n.t('discovery.sellForm.shippingSection.internationalShippingCard.header')} >
        {suggestedShippingRegions.length > 0 && (
          <SellFormCallout
            imageSrc={TrendingSvg}
            imageAlt={I18n.t('discovery.sellForm.shippingSection.prefersReverbShippingLabelCard.valueProp.iconAlt')}
          >
            <div className="size-90 ml-2">
              <Translate text="discovery.sellForm.shippingSection.internationalShippingCard.suggestedRegions">
                <div className="pl-2 mtb-half">
                  <ul className="cms-ul mb-0">
                    {suggestedShippingRegions.map(({ regionName }) => (
                      <li key={regionName}>
                        {regionName}
                      </li>
                    ))}
                  </ul>
                </div>
              </Translate>
            </div>
          </SellFormCallout>
        )}

        {nonDomesticShippingRates.map((sr) => <InternationalShippingPrice key={sr.regionCode} shippingRate={sr}/>)}

        <ShippingRegionSelector
          shippingRegions={shippingRegions}
          onSelect={(r) => {
            if (r.regionCode) {
              dispatch({
                type: ActionType.UPDATE_FIELD,
                fieldKey: Field.SHIPPING_RATES,
                value: upsertShippingRate(formState[Field.SHIPPING_RATES], buildBlankShippingRate(r.regionCode)),
              });
            }
          }}
          disabledShippingRegionCodes={formState[Field.SHIPPING_RATES].map(({ regionCode }) => regionCode)}
          value={null}
          disabled={loading}
        />

        <div className="scaling-mb-3">
          <p className="mt-1 size-90 lh-120 mb-0">
            {I18n.t('discovery.dashboard.selling.shippingRates.addShippingLocationExplainer')}
            <CoreLink
              to={UrlHelpers.internationalShippingGuidePath}
              target="_blank"
              className="ml-space"
            >
              {I18n.t('discovery.dashboard.selling.shippingRates.learnMore')}
            </CoreLink>
          </p>
        </div>
      </SellFormSection.FormSection>
    </SellFormSection.Card>
  );
}

export default InternationalShippingCard;
